import { supabase } from '../lib/supabase';

/**
 * Fix orphaned company by creating missing user_company_relationship
 * This handles cases where company was created but relationship creation failed
 */
async function fixOrphanedCompany(companyId: string, userId: string): Promise<void> {
  // Check if relationship already exists
  const { data: existingRel } = await supabase
    .from('user_company_relationships')
    .select('id')
    .eq('user_id', userId)
    .eq('company_id', companyId)
    .maybeSingle();

  if (existingRel) {
    // Relationship exists, nothing to fix
    return;
  }

  // Get owner role ID
  const { data: ownerRole } = await supabase
    .from('system_roles')
    .select('system_role_id')
    .eq('role', 'owner')
    .single();

  if (!ownerRole) {
    throw new Error('Owner role not found');
  }

  // Create the missing relationship
  const { error: relError } = await supabase
    .from('user_company_relationships')
    .insert({
      user_id: userId,
      company_id: companyId,
      role_id: ownerRole.system_role_id,
      onboarding_complete: false,
    });

  if (relError) {
    throw new Error(`Failed to create company relationship: ${relError.message}`);
  }

  // Update last_selected_company
  await supabase
    .from('user_profiles')
    .update({ last_selected_company: companyId })
    .eq('id', userId);
}

/**
 * Save company data incrementally during onboarding
 * - If companyId exists, UPDATE the existing company
 * - If companyId doesn't exist, CREATE a new company using RPC function
 * - Handles orphaned companies (company exists but no relationship)
 * 
 * This matches mobile app behavior where data is saved at each step
 */
export async function saveCompanyData(
  companyId: string | null | undefined,
  companyData: {
    name: string;
    slogan?: string | null;
    ein?: string | null;
    contactFirstName?: string | null;
    contactLastName?: string | null;
    email?: string | null;
    phone?: string | null;
    street?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    place_id?: string | null;
    logoUrl?: string | null;
  }
): Promise<{ companyId: string; created: boolean }> {
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be logged in to save company data');
  }

  // If companyId exists, UPDATE the company
  if (companyId) {
    // First, check if this company has a relationship (might be orphaned)
    const { data: relationship } = await supabase
      .from('user_company_relationships')
      .select('id, system_roles(role)')
      .eq('user_id', user.id)
      .eq('company_id', companyId)
      .maybeSingle();

    // If no relationship exists, this is an orphaned company - fix it
    if (!relationship) {
      await fixOrphanedCompany(companyId, user.id);
      // Refresh session to get updated JWT
      await supabase.auth.refreshSession();
    } else {
      // Verify user has permission to update this company
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const role = (relationship as any)?.system_roles?.role;
      if (!role || !['owner', 'admin'].includes(role)) {
        throw new Error('You do not have permission to update this company');
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updatePayload: any = {};
    
    // Only include fields that have values (partial update)
    if (companyData.name) updatePayload.name = companyData.name;
    if (companyData.slogan !== undefined) updatePayload.slogan = companyData.slogan || null;
    if (companyData.ein !== undefined) updatePayload.ein = companyData.ein || null;
    if (companyData.contactFirstName !== undefined) updatePayload.contact_fn = companyData.contactFirstName || null;
    if (companyData.contactLastName !== undefined) updatePayload.contact_ln = companyData.contactLastName || null;
    if (companyData.email !== undefined) updatePayload.company_email = companyData.email || null;
    if (companyData.phone !== undefined) updatePayload.phone = companyData.phone || null;
    if (companyData.street !== undefined) updatePayload.address = companyData.street || null;
    if (companyData.street2 !== undefined) updatePayload.address2 = companyData.street2 || null;
    if (companyData.city !== undefined) updatePayload.city = companyData.city || null;
    if (companyData.state !== undefined) updatePayload.state = companyData.state || null;
    if (companyData.zip !== undefined) updatePayload.zip = companyData.zip || null;
    if (companyData.latitude !== undefined) updatePayload.geofence_lat = companyData.latitude || null;
    if (companyData.longitude !== undefined) updatePayload.geofence_lng = companyData.longitude || null;
    // Save place_id to companies table (also stored on locations for location-specific reviews)
    if (companyData.place_id !== undefined) updatePayload.place_id = companyData.place_id || null;
    if (companyData.logoUrl !== undefined) updatePayload.logo_url = companyData.logoUrl || null;

    const { error: updateError } = await supabase
      .from('companies')
      .update(updatePayload)
      .eq('id', companyId);

    if (updateError) {
      throw new Error(`Failed to update company: ${updateError.message}`);
    }

    return { companyId, created: false };
  }

  // No companyId exists - CREATE a new company using RPC function
  // This handles: company insert, user_company_relationship, last_selected_company
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: newCompanyId, error: rpcError } = await (supabase.rpc as any)('create_company_for_user', {
    p_name: companyData.name || 'New Gym (Draft)',
    p_slogan: companyData.slogan || null,
    p_ein: companyData.ein || null,
    p_contact_fn: companyData.contactFirstName || null,
    p_contact_ln: companyData.contactLastName || null,
    p_company_email: companyData.email || null,
    p_phone: companyData.phone || null,
    p_address: companyData.street || null,
    p_address2: companyData.street2 || null,
    p_city: companyData.city || null,
    p_state: companyData.state || null,
    p_zip: companyData.zip || null,
    p_logo_url: companyData.logoUrl || null,
  });

  if (rpcError) {
    throw new Error(`Failed to create company: ${rpcError.message}`);
  }

  if (!newCompanyId) {
    throw new Error('No company ID returned from server');
  }

  // Refresh JWT to get new company_id claim
  await supabase.auth.refreshSession();

  return { companyId: newCompanyId as string, created: true };
}
